import type { WebMcpRegistry } from '@bomdisoft/webmcp-core';

/**
 * Publishes the live registry outside React's tree so a tool host does not have
 * to be a descendant of the provider.
 *
 * Tool hosts used to reach the registry through context alone, which meant a
 * host mounted outside — or before — the provider registered nothing, silently:
 * `useWebMcpTool` simply saw a null context and returned. That coupling is what
 * forced the provider to wrap an entire page tree, dragging the tool registry
 * and its zod dependency into the initial bundle of every route.
 *
 * With the registry published here instead, a host can mount anywhere and at any
 * time: it subscribes, and registers as soon as a provider appears.
 */
type Listener = (registry: WebMcpRegistry | null) => void;

let currentRegistry: WebMcpRegistry | null = null;
const listeners = new Set<Listener>();

export const publishRegistry = (registry: WebMcpRegistry | null): void => {
  if (currentRegistry === registry) return;
  currentRegistry = registry;
  for (const listener of listeners) listener(registry);
};

export const getRegistry = (): WebMcpRegistry | null => currentRegistry;

export const subscribeRegistry = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Server render has no registry and no subscribers; keeps useSyncExternalStore honest. */
export const getServerRegistry = (): WebMcpRegistry | null => null;
